import React, { MouseEventHandler, useContext, useEffect, useRef, useState } from "react"
import { Fragment } from "react"
import { Menu, Transition } from "@headlessui/react"
import { ChevronDownIcon, EnvelopeIcon } from "@heroicons/react/20/solid"
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline"
import { AdminContext } from "~/contexts/AdminContext"
import ADMIN_NAVBAR_ITEMS from "../../../constants/admin_navbar_items"
import { useMe } from "~/api2/hooks/useMe"
import { useMeCamps } from "~/api2/hooks/useMeCamps"
import { useCampContext } from "~/contexts/CampProvider"
import api from "~/api2"
import { useDebouncedCallback } from "use-debounce"
import classNames from "~/utils/classNames"
import { useCamp } from "~/api2/hooks/useCamp"
import eAdminUserRole from "~/enums/eAdminUserRole"

interface ComponentProps {
  handleLogout: MouseEventHandler<HTMLButtonElement>
}

export default function AdminDropdown(props: ComponentProps) {
  const { setCampId } = useCampContext()
  const {
    index: { data: camp },
  } = useCamp()
  const { currentPage, setCurrentPage, setCamp, reloadAccessibleCamps } = useContext(AdminContext)
  const { data: userCamps, isLoading } = useMeCamps()
  const { data: user } = useMe()

  const currentUserRole = user?.roles?.[0]
  const currentUserEmail = user?.email
  const campName = camp?.name

  // please remove after routes migration ``````````````````
  useEffect(() => {
    reloadAccessibleCamps()
    fetchFullCamp(camp?.id)
  }, [camp?.id])

  const handleAccountSwitch = (id: string) => {
    setCampId(id)
  }

  //remove after migration
  const fetchFullCamp = useDebouncedCallback((campId: string) => {
    api.Me.camp(campId).then((camp) => {
      setCamp(camp)
    })
  }, 100)

  if (!camp || !campName) return

  return (
    <Menu as="div" className="relative inline-block text-left" data-testid="nav-bar-admin-dropdown-menu">
      <div>
        <Menu.Button
          data-testid="nav-bar-admin-dropdown-menu-button"
          className="inline-flex rounded-md border border-gray-300 bg-white items-center overflow-visible px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 open-sans"
        >
          <div className="w-32 text-left xsm:w-full truncate">{campName}</div>
          <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-40 mt-2 origin-top-right w-80 divide-y divide-gray-100 rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="px-4 py-3">
            <p className="text-md">Signed in as</p>
            <p className="truncate text-md font-medium text-gray-900">{currentUserEmail}</p>
          </div>
          <div className="lg:hidden py-1">
            {ADMIN_NAVBAR_ITEMS.map((item, index) => {
              const isCurrent = currentPage == item.key
              if (!(currentUserRole == "user" && item.key == "reports")) {
                return (
                  <Menu.Item key={item.name} data-testid={`nav-bar-admin-dropdown-menu-item-${index}`}>
                    {({ close }) => (
                      <a
                        onClick={(e) => {
                          close()
                          e.preventDefault()
                          setCurrentPage(item.key)
                          e.preventDefault()

                          localStorage.setItem("from", window.location.pathname)
                          localStorage.setItem("toNav", item.key)
                          localStorage.setItem("toTab", "camp")
                          if (window.location.pathname !== "/admin/reservations") {
                            window.location.href = "/admin/reservations"
                          }
                        }}
                        href={isCurrent ? undefined : item.href}
                        target={isCurrent ? undefined : "_blank"}
                        className={classNames(
                          isCurrent
                            ? "bg-gray-100 text-gray-900 hover:text-gray-900"
                            : "text-gray-900 hover:bg-gray-100 hover:text-gray-900",
                          "px-4 py-3 text-md flex",
                        )}
                      >
                        {item.name}
                      </a>
                    )}
                  </Menu.Item>
                )
              }
            })}
          </div>

          {userCamps?.camps?.length > 0 ? (
            <div className="py-1 overflow-auto max-h-72">
              {(userCamps?.camps).map((userAvailableCamp) => {
                if (userAvailableCamp.name != camp?.name) {
                  return (
                    <Menu.Item key={userAvailableCamp.id}>
                      {({ active, close }) => (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            if (!isLoading) handleAccountSwitch(userAvailableCamp.id)
                            close()
                          }}
                          className={classNames(
                            active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                            isLoading && "text-opacity-30",
                            "block px-4 py-3 text-md",
                          )}
                        >
                          Switch to <span className="font-medium">{userAvailableCamp.name}</span>
                        </a>
                      )}
                    </Menu.Item>
                  )
                }
              })}
            </div>
          ) : (
            <></>
          )}

          <div className="py-1">
            {[eAdminUserRole.ParkAdmin, eAdminUserRole.Admin].includes(currentUserRole) && (
              <Menu.Item data-testid="nav-bar-admin-dropdown-menu-item-settings">
                {({ active }) => (
                  <a
                    onClick={() => {
                      localStorage.setItem("from", window.location.pathname)
                      localStorage.setItem("toNav", "settings")
                      localStorage.setItem("toTab", "camp")
                      if (window.location.pathname !== "/admin/reservations") {
                        location.href = "/admin/reservations"
                      } else {
                        setCurrentPage("settings")
                      }
                    }}
                    className={classNames(
                      "hidden lg:block",
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "block px-4 py-3 text-md cursor-pointer",
                    )}
                  >
                    Settings
                  </a>
                )}
              </Menu.Item>
            )}
            <Menu.Item data-testid="nav-bar-admin-dropdown-menu-item-booker">
              {({ active }) => (
                <a
                  href={`/camps/${camp?.slug}`}
                  target="_blank"
                  className={classNames(
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                    "block px-4 py-3 text-md",
                  )}
                >
                  Booker
                  <ArrowTopRightOnSquareIcon className="inline h-4 mr-2 ml-1.5 -mt-0.5" />
                </a>
              )}
            </Menu.Item>
            <Menu.Item data-testid="nav-bar-admin-dropdown-menu-item-help">
              {({ active }) => (
                <a
                  href={`https://help.poweredbypark.com/`}
                  target="_blank"
                  className={classNames(
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                    "block px-4 py-3 text-md",
                  )}
                >
                  Help Center
                  <ArrowTopRightOnSquareIcon className="inline h-4 mr-2 ml-1.5 -mt-0.5" />
                </a>
              )}
            </Menu.Item>
          </div>
          <div className="py-1">
            <form method="POST" action="#">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="submit"
                    onClick={props.handleLogout}
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "block w-full px-4 py-3 text-left text-md open-sans",
                    )}
                  >
                    Sign out
                  </button>
                )}
              </Menu.Item>
            </form>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
